import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { FinishApprovalData } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES, POPUP_COLORS } from '../../constants/popupStyles';

interface CaseClosureApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId?: number;
    token: string;
    /** Resolve a user id to a display name (for Request By). */
    getUserName?: (userId: number) => string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

// Hardcoded Clicks-update instructions (Formal mode only) — verbatim per design PDF page 5.
const CLICKS_INSTRUCTIONS_TITLE = 'הוראות עדכון מערכת Clicks';
const CLICKS_INSTRUCTIONS_INTRO = 'למערכת Clicks יש להעתיק את המידע הבא:';
const CLICKS_INSTRUCTIONS_BULLETS = [
    'להעתיק את סיכום מנהל הטיפול מתחילת הסיכום ועד (כולל) פרק "תולדות חיים וחברה" ללא הסטטוס המנטלי!',
    'להעתיק את הערות סיום הטיפול (Psychiatrist Closure Notes) ולהדביקן באזור סיכום סיום הטיפול במערכת Clicks.',
];

export const CaseClosureApprovalModal: React.FC<CaseClosureApprovalModalProps> = ({
    isOpen,
    onClose,
    patientId,
    token,
    getUserName,
    onSuccess,
    onStatusMessage,
}) => {
    const [data, setData] = useState<FinishApprovalData | null>(null);
    const [closureNotes, setClosureNotes] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load finish-approval data when the modal opens
    useEffect(() => {
        const loadData = async () => {
            if (!isOpen || !patientId || !token) return;

            setIsLoading(true);
            onStatusMessage?.({ type: 'loading', text: 'Loading case closure data...' });

            try {
                const result = await apiService.getFinishApprovalData(token, patientId);
                setData(result);
                // Prefill the editable box with the backend-provided default/stored notes.
                setClosureNotes(result.finish_request_approval_notes || '');
                onStatusMessage?.(null);
            } catch (error: any) {
                console.error('Failed to load case closure data:', error);
                onStatusMessage?.({
                    type: 'error',
                    text: error.message || 'Failed to load case closure data',
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, patientId, token]);

    // Format date as DD/MM/YYYY
    const formatDate = (timestamp: number | null) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleDateString('en-GB');
    };

    const handleClose = () => {
        setData(null);
        setClosureNotes('');
        onClose();
    };

    // Save = persist closure_notes only, no status change (save_only:true, is_formal:false)
    const handleSave = async () => {
        if (!patientId || !token || !data) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Saving closure notes...' });

        try {
            await apiService.finishApproval({
                token,
                patient_id: patientId,
                save_only: true,
                is_formal: false,
                finish_reason: data.finish_reason,
                closure_notes: closureNotes,
            });

            onStatusMessage?.({ type: 'success', text: 'Closure notes saved successfully' });
            setTimeout(() => onStatusMessage?.(null), 3000);
        } catch (error: any) {
            console.error('Failed to save closure notes:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to save closure notes',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Approve = save notes + set status approved (save_only:false, is_formal:<mode>)
    const handleApprove = async () => {
        if (!patientId || !token || !data) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Approving case closure...' });

        try {
            await apiService.finishApproval({
                token,
                patient_id: patientId,
                save_only: false,
                is_formal: isFormal,
                finish_reason: data.finish_reason,
                closure_notes: closureNotes,
            });

            onStatusMessage?.({ type: 'success', text: 'Case closed successfully' });
            setTimeout(() => onStatusMessage?.(null), 3000);
            onSuccess?.();
            handleClose();
        } catch (error: any) {
            console.error('Failed to approve case closure:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to approve case closure',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(closureNotes);
    };

    const requestByName = data?.request_by != null
        ? (getUserName?.(data.request_by) || `User #${data.request_by}`)
        : '';

    // Formal vs Informal is decided by the backend (Maccabi AND originating status "pending").
    const isFormal = !!data?.is_formal;

    const primaryButtonLabel = isFormal ? 'Update In Clicks' : 'Approve & Close Case';

    return (
        <ModalWrapper modalId="CaseClosureApprovalModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Case Closure Approval"
            isLoading={isSubmitting}
            width="!max-w-4xl"
            className="max-h-[90vh] flex flex-col"
            compactHeader={true}
        >
            <div className="flex-1 overflow-y-auto px-3 pt-1 pb-3 min-h-0">
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#366A75]"></div>
                    </div>
                ) : data ? (
                    <>
                        {/* Header block */}
                        <div className="flex gap-x-6 text-base text-[#366A75] mb-3">
                            {/* Left column */}
                            <div className="flex flex-col gap-y-1 flex-1">
                                <div><span className="font-bold">Name:</span> {data.name || '—'}</div>
                                <div><span className="font-bold">Id (TZ):</span> {data.patient_tz || '—'}</div>
                                <div><span className="font-bold">HM_ID:</span> {data.HM_ID || '—'}</div>
                            </div>
                            {/* Right column */}
                            <div className="flex flex-col gap-y-1 flex-1">
                                <div><span className="font-bold">Finish Requested On:</span> {formatDate(data.requested_date) || '—'}</div>
                                <div><span className="font-bold">Request By:</span> {requestByName || '—'}</div>
                                <div><span className="font-bold">Finish Reason:</span> {data.finish_reason || '—'}</div>
                                <div><span className="font-bold">Finish Notes:</span> {data.finish_notes || '—'}</div>
                            </div>
                        </div>

                        {/* Case Manager Intake Summary (read-only) */}
                        <div className="mb-3">
                            <label className="block text-lg font-bold text-[#366A75] mb-1">
                                Case Manager Intake Summary
                            </label>
                            <textarea
                                value={data.cm_summary || ''}
                                readOnly
                                className={`${POPUP_STYLES.input} h-[260px] resize-none bg-gray-50 cursor-default`}
                                dir="rtl"
                                style={{
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '15px',
                                    lineHeight: '1.5',
                                    color: POPUP_COLORS.titleText,
                                }}
                            />
                        </div>

                        {/* Clicks-update instructions — Formal mode only */}
                        {isFormal && (
                            <div
                                className="mb-3 p-3 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] text-[#366A75]"
                                dir="rtl"
                                style={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', lineHeight: '1.6' }}
                            >
                                <div className="font-bold mb-1">{CLICKS_INSTRUCTIONS_TITLE}</div>
                                <div className="mb-1">{CLICKS_INSTRUCTIONS_INTRO}</div>
                                <ul className="list-disc pr-5 space-y-1">
                                    {CLICKS_INSTRUCTIONS_BULLETS.map((bullet, i) => (
                                        <li key={i}>{bullet}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Psychiatric Closure Notes */}
                        <div className="mb-1">
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-lg font-bold text-[#366A75]">
                                    Psychiatric Closure Notes
                                </label>
                                <button
                                    onClick={handleCopyToClipboard}
                                    className="text-[#366A75] hover:text-[#2D5A60]"
                                    title="Copy to clipboard"
                                    type="button"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                    </svg>
                                </button>
                            </div>
                            <textarea
                                value={closureNotes}
                                onChange={(e) => setClosureNotes(e.target.value)}
                                className={`${POPUP_STYLES.input} h-[200px] resize-none`}
                                dir="rtl"
                                disabled={isSubmitting}
                                style={{
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '15px',
                                    lineHeight: '1.5',
                                    color: POPUP_COLORS.titleText,
                                }}
                            />
                        </div>
                    </>
                ) : null}
            </div>

            {/* Footer Actions — right→left (RTL): Cancel, Save, primary */}
            <div className="flex items-center justify-center gap-4 px-4 py-1 flex-shrink-0">
                <button
                    data-ai-id="case-closure-approve"
                    onClick={handleApprove}
                    disabled={isSubmitting || isLoading || !data}
                    className={`${POPUP_STYLES.button} text-base`}
                >
                    {primaryButtonLabel}
                </button>
                <button
                    onClick={handleSave}
                    disabled={isSubmitting || isLoading || !data}
                    className={`${POPUP_STYLES.button} text-base`}
                >
                    Save
                </button>
                <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className={`${POPUP_STYLES.button} text-base`}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
